/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import '../style/VehicleMonitoringStyle.css'
import SplitPane, {Pane} from 'split-pane-react'
import 'split-pane-react/esm/themes/default.css'
import {MapComponent} from '../UI/Map/MapComponent'
import {
  ActiveFilterChip,
  filterInitials,
  FilterOptions,
  VehicleMonitoringAdvanceSearch,
} from '../UI/AdvanceSearch/VehicleMonitoring'
import {VehicleList, GpsStatus, VehicleListItem} from '../UI/VehicleMonitoring/VehicleList'
import {VehicleDetailPanel} from '../UI/VehicleMonitoring/VehicleDetailPanel'
import {JobTimelineStrip} from '../UI/VehicleMonitoring/JobTimelineStrip'
import {useDataLayerValue} from '../../DataLayer'
import {useDispatch, useSelector} from 'react-redux'
import {useMonitoringMapQuery} from '../redux/network/MonitoringMap'
import {
  resetState,
  setGPSVehiclesDetails,
  setMapViewVehicle,
  setMarkers,
} from '../redux/MapDataSlice'
import {useFetchSIDTokenQuery, useGetGPSVehicleDetailsQuery} from '../redux/network/Wialon'
import {skipToken} from '@reduxjs/toolkit/query'

type Props = {
  allJobs: any
  fetchAllJobs: () => void
}

function resolveGpsStatus(gpsItem: any): GpsStatus {
  if (!gpsItem?.pos || gpsItem.pos.x == null || gpsItem.pos.y == null) return 'no_gps'
  if (gpsItem.pos.s > 0) return 'moving'
  if (gpsItem.pos.s === 0) return 'stopped'
  return 'no_gps'
}

const VehicleMonitoring: React.FC<Props> = () => {
  const [{company}] = useDataLayerValue()

  const {uniqueDataRedux, MapViewVehicle, ActivePage, GPSVehiclesDetails} = useSelector(
    (state: any) => {
      return {
        uniqueDataRedux: state.MapData?.uniqueDataRedux || [],
        MapViewVehicle: state.MapData?.MapViewVehicle || [],
        ActivePage: state.ActivePage?.ActivePage || '',
        GPSVehiclesDetails: state.MapData?.GPSVehiclesDetails || [],
      }
    }
  )

  const [sizes, setSizes] = useState<(number | string)[]>([280, 'auto'])
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(filterInitials)
  const [activeFilterChips, setActiveFilterChips] = useState<ActiveFilterChip[]>([])
  const [filterResetToken, setFilterResetToken] = useState(0)
  const [selectedFocusId, setSelectedFocusId] = useState<number>(0)
  const [selectedMarkerId, setSelectedMarkerId] = useState<number>(0)
  const [followVehicleId, setFollowVehicleId] = useState<number>(0)
  const [detailOpen, setDetailOpen] = useState(false)
  const [timelineOpen, setTimelineOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [mobileTab, setMobileTab] = useState<'list' | 'map'>('map')
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 992 : true
  )
  const dispatch = useDispatch()

  const itemIdString = useMemo(
    () => (MapViewVehicle || []).map((id: number) => `=${id}`).join(','),
    [MapViewVehicle]
  )

  const requestBody = {
    flagStatus: filterOptions.flagStatus,
    requestId: filterOptions.requestId !== '' ? filterOptions.requestId : null,
    fromDate: filterOptions.fromDate !== '' ? filterOptions.fromDate : null,
    toDate: filterOptions.toDate !== '' ? filterOptions.toDate : null,
    docConfirmation: filterOptions.docConfirmation,
    jobStatusCodes: filterOptions.jobStatusCodes,
    requestCustomer: filterOptions.requestCustomer,
    shipper: filterOptions.shipper,
    JobCode: filterOptions.jobCode !== '' ? filterOptions.jobCode : null,
    vehicleRegNumber: filterOptions.vehicleRegNumber !== '' ? filterOptions.vehicleRegNumber : null,
    vehicleType: filterOptions.vehicleType,
    statusCode: filterOptions.statusCode !== '' ? filterOptions.statusCode : null,
    CT_FK_OperatingTypeCode:
      filterOptions.cT_FK_OperatingTypeCode !== '' && filterOptions.cT_FK_OperatingTypeCode != null
        ? Number(filterOptions.cT_FK_OperatingTypeCode)
        : null,
    CT_FK_CustomerSpecificationCode:
      filterOptions.cT_FK_CustomerSpecificationCode !== '' &&
      filterOptions.cT_FK_CustomerSpecificationCode != null
        ? Number(filterOptions.cT_FK_CustomerSpecificationCode)
        : null,
  }

  const {isFetching: monitoringMapLoading, refetch: refetchMonitoringMap} = useMonitoringMapQuery(
    {company: company, requestBody},
    {
      refetchOnMountOrArgChange: true,
      pollingInterval: 0,
      skip: false,
    }
  )

  const {data: sid, error: sidError, refetch: refetchSID} = useFetchSIDTokenQuery(
    {},
    {
      refetchOnMountOrArgChange: false,
      pollingInterval: 0,
      skip: MapViewVehicle.length === 0,
    }
  )

  const {
    data: gpsVehicleDetails,
    error: gpsVehicleDetailsError,
  } = useGetGPSVehicleDetailsQuery(sid ? {itemIdsArray: itemIdString, sID: sid} : skipToken, {
    refetchOnMountOrArgChange: true,
    pollingInterval: 6000,
    skip:
      !sid ||
      ActivePage !== '/vehicle-monitoring' ||
      MapViewVehicle.length === 0 ||
      !itemIdString ||
      itemIdString === '',
  })

  useEffect(() => {
    dispatch(resetState())
  }, [dispatch])

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 992)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (ActivePage !== '/vehicle-monitoring') return

    if (MapViewVehicle.length === 0 || !itemIdString) {
      dispatch(setGPSVehiclesDetails([]))
      dispatch(setMarkers([]))
      return
    }

    if (gpsVehicleDetails) {
      dispatch(setGPSVehiclesDetails(gpsVehicleDetails))
      dispatch(setMarkers(gpsVehicleDetails))
    }

    if (gpsVehicleDetailsError || sidError) {
      refetchSID()
    }
  }, [
    ActivePage,
    MapViewVehicle.length,
    itemIdString,
    gpsVehicleDetails,
    gpsVehicleDetailsError,
    sidError,
    refetchSID,
    dispatch,
  ])

  const vehicleMonitoringData: VehicleListItem[] = useMemo(() => {
    return uniqueDataRedux.map((vehicle: any) => {
      const itemId = vehicle?.itemID ?? vehicle?.ItemID
      const gpsItem = GPSVehiclesDetails?.find((item: any) => item?.id === itemId)
      const gpsStatus = resolveGpsStatus(gpsItem)
      return {
        itemId,
        vehicleNumber: vehicle?.registrationNo ?? vehicle?.RegistrationNo ?? '',
        requestId: vehicle?.requestId ?? vehicle?.RequestId ?? '',
        jobStatus: vehicle?.jobStatus ?? vehicle?.JobStatus ?? null,
        pickupLocation: vehicle?.pickupLocation ?? vehicle?.PickupLocation ?? null,
        deliveryLocation: vehicle?.deliveryLocation ?? vehicle?.DeliveryLocation ?? null,
        atPickup: vehicle?.atPickup ?? vehicle?.ATPickup ?? null,
        dtPickup: vehicle?.dtPickup ?? vehicle?.DTPickup ?? null,
        atDelivery: vehicle?.atDelivery ?? vehicle?.ATDelivery ?? null,
        dtDelivery: vehicle?.dtDelivery ?? vehicle?.DTDelivery ?? null,
        viaLocationList: vehicle?.viaLocationList ?? vehicle?.ViaLocationList ?? [],
        speed: gpsItem?.pos?.s != null ? gpsItem.pos.s : null,
        gpsStatus,
      }
    })
  }, [uniqueDataRedux, GPSVehiclesDetails])

  const searchData = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return vehicleMonitoringData
    return vehicleMonitoringData.filter(
      (vehicle) =>
        vehicle.vehicleNumber.toLowerCase().includes(q) ||
        String(vehicle.requestId).toLowerCase().includes(q)
    )
  }, [vehicleMonitoringData, searchQuery])

  const selectedDetailVehicle = useMemo(() => {
    const id = selectedMarkerId || selectedFocusId
    if (!id) return null
    const base = vehicleMonitoringData.find((v) => v.itemId === id)
    if (!base) return null
    const gpsItem = GPSVehiclesDetails?.find((item: any) => item?.id === id)
    return {
      ...base,
      lastUpdateUnix: gpsItem?.pos?.t ?? null,
      mileage: gpsItem?.lmsg?.p?.mileage ?? null,
      battery: gpsItem?.lmsg?.p?.battery ?? null,
    }
  }, [selectedMarkerId, selectedFocusId, vehicleMonitoringData, GPSVehiclesDetails])

  const selectVehicle = (itemId: number) => {
    setSelectedMarkerId(itemId)
    setSelectedFocusId(itemId)
    setDetailOpen(true)
    if (followVehicleId && followVehicleId !== itemId) {
      setFollowVehicleId(itemId)
    }
    if (!isDesktop) setMobileTab('map')
  }

  const closeDetailPanel = () => {
    setDetailOpen(false)
  }

  const clearSelection = () => {
    setSelectedMarkerId(0)
    setSelectedFocusId(0)
    setFollowVehicleId(0)
    setDetailOpen(false)
    setTimelineOpen(false)
  }

  const showAllOnMap = useCallback(() => {
    const ids = searchData.map((v) => v.itemId)
    dispatch(setMapViewVehicle(ids))
  }, [searchData, dispatch])

  const toggleFollow = () => {
    const target = followVehicleId || selectedMarkerId || selectedFocusId
    if (!target) return
    if (followVehicleId === target) {
      setFollowVehicleId(0)
    } else {
      setFollowVehicleId(target)
      setSelectedFocusId(target)
      setSelectedMarkerId(target)
      setDetailOpen(true)
    }
  }

  const layoutCSS = {
    height: '100%',
    overflow: 'hidden' as const,
    minWidth: '100px',
    background: '#e4e6ef',
  }

  const mapPane = (
    <div className={`vm-map-pane position-relative h-100 ${timelineOpen ? 'vm-map-pane--timeline' : ''}`}>
      <MapComponent
        selectedFocusId={selectedFocusId}
        selectedMarkerId={selectedMarkerId}
        setSelectedMarkerId={(id) => {
          if (id) {
            selectVehicle(id)
          } else {
            setSelectedMarkerId(0)
          }
        }}
        followVehicleId={followVehicleId}
        showLegend
        showMapToolbar
        labelMode='zoomOrSelected'
        mapContainerHeight='100%'
        onClearSelection={clearSelection}
        onToggleFollow={toggleFollow}
        onShowAll={showAllOnMap}
        onMarkerSelect={(id) => {
          if (id) selectVehicle(id)
        }}
        infoVehicle={
          selectedDetailVehicle
            ? {
                requestId: selectedDetailVehicle.requestId,
                jobStatus: selectedDetailVehicle.jobStatus,
                vehicleNumber: selectedDetailVehicle.vehicleNumber,
              }
            : null
        }
      />
      <JobTimelineStrip
        open={timelineOpen && !!selectedDetailVehicle}
        vehicle={selectedDetailVehicle}
        onClose={() => setTimelineOpen(false)}
        onRefresh={() => {
          void refetchMonitoringMap()
        }}
        isRefreshing={monitoringMapLoading}
      />
      <VehicleDetailPanel
        open={detailOpen && !!selectedDetailVehicle}
        variant={isDesktop ? 'drawer' : 'sheet'}
        vehicle={selectedDetailVehicle}
        isFollowing={!!followVehicleId && followVehicleId === selectedDetailVehicle?.itemId}
        timelineOpen={timelineOpen}
        onClose={closeDetailPanel}
        onFocus={() => {
          if (selectedDetailVehicle) {
            setSelectedFocusId(selectedDetailVehicle.itemId)
            setSelectedMarkerId(selectedDetailVehicle.itemId)
          }
        }}
        onToggleFollow={toggleFollow}
        onToggleTimeline={() => setTimelineOpen((v) => !v)}
      />
    </div>
  )

  const listPane = (
    <VehicleList
      vehicles={searchData}
      mapViewVehicle={MapViewVehicle}
      selectedFocusId={selectedFocusId}
      selectedMarkerId={selectedMarkerId}
      searchQuery={searchQuery}
      isLoading={monitoringMapLoading}
      showingOnMapCount={MapViewVehicle.length}
      totalCount={vehicleMonitoringData.length}
      onSearchChange={setSearchQuery}
      onToggleVehicle={(itemId) => {
        if (MapViewVehicle.includes(itemId)) {
          dispatch(setMapViewVehicle(MapViewVehicle.filter((code: number) => code !== itemId)))
        } else {
          dispatch(setMapViewVehicle([...MapViewVehicle, itemId]))
        }
      }}
      onToggleAll={() => {
        const filteredIds = searchData.map((v) => v.itemId)
        const allSelected =
          filteredIds.length > 0 && filteredIds.every((id) => MapViewVehicle.includes(id))
        if (allSelected) {
          dispatch(
            setMapViewVehicle(MapViewVehicle.filter((id: number) => !filteredIds.includes(id)))
          )
        } else {
          const merged = Array.from(new Set([...MapViewVehicle, ...filteredIds]))
          dispatch(setMapViewVehicle(merged))
        }
      }}
      onSelectVehicle={selectVehicle}
      onToggleFocus={(itemId) => {
        if (selectedFocusId === itemId) {
          setSelectedFocusId(0)
          if (followVehicleId === itemId) setFollowVehicleId(0)
        } else {
          selectVehicle(itemId)
        }
      }}
    />
  )

  return (
    <div className='card vm-monitoring-card'>
      <div className='card-body p-0'>
        <div className='d-flex justify-content-between flex-wrap'>
          <div className='accordion flex-grow-1' id='kt_accordion_1'>
            <div className='accordion-item px-5 py-3 border-0'>
              <h3 className='accordion-header py-0' id='kt_accordion_1_header_1'>
                <button
                  className='accordion-button fw-semibold collapsed text-gray-400'
                  style={{
                    paddingTop: '.65rem',
                    paddingBottom: '.65rem',
                    fontSize: '1rem',
                  }}
                  type='button'
                  data-bs-toggle='collapse'
                  data-bs-target='#kt_accordion_1_body_1'
                  aria-expanded='false'
                  aria-controls='kt_accordion_1_body_1'
                >
                  <i className='bi bi-sliders me-2'></i>
                  Advance Search
                </button>
              </h3>
              <VehicleMonitoringAdvanceSearch
                setFilterOptions={setFilterOptions}
                onActiveFiltersChange={setActiveFilterChips}
                resetToken={filterResetToken}
              />
              {activeFilterChips.length > 0 && (
                <div className='vm-filter-chips px-1 pt-2'>
                  {activeFilterChips.map((chip) => (
                    <span key={chip.id} className='vm-filter-chip' title={`${chip.label}: ${chip.value}`}>
                      <strong>{chip.label}:</strong> {chip.value}
                    </span>
                  ))}
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary vm-filter-chip-clear'
                    onClick={() => setFilterResetToken((t) => t + 1)}
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='separator'></div>

        {!isDesktop && (
          <div className='vm-mobile-tabs'>
            <button
              type='button'
              className={`vm-mobile-tab ${mobileTab === 'list' ? 'is-active' : ''}`}
              onClick={() => setMobileTab('list')}
            >
              List
            </button>
            <button
              type='button'
              className={`vm-mobile-tab ${mobileTab === 'map' ? 'is-active' : ''}`}
              onClick={() => setMobileTab('map')}
            >
              Map
            </button>
            <span className='vm-mobile-tabs__count'>
              {MapViewVehicle.length}/{vehicleMonitoringData.length}
            </span>
          </div>
        )}

        <div className='vm-main-stage'>
          {isDesktop ? (
            <SplitPane
              split='vertical'
              sizes={sizes}
              onChange={(nextSizes) => setSizes(nextSizes)}
              sashRender={() => <div className='sash' />}
            >
              <Pane minSize={180} maxSize='45%'>
                <div style={layoutCSS}>{listPane}</div>
              </Pane>
              <div style={{height: '100%'}}>{mapPane}</div>
            </SplitPane>
          ) : (
            <div className='vm-mobile-stage h-100'>
              {mobileTab === 'list' ? (
                <div className='h-100' style={{background: '#e4e6ef'}}>
                  {listPane}
                </div>
              ) : (
                mapPane
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export {VehicleMonitoring}
